import {gtag, pushToDataLayer} from "./analytics";

// The user's cookie choice, stored in a first-party cookie that
// public/index.html reads synchronously to set Consent Mode defaults before
// GTM loads. Bump CONSENT_VERSION there when the purposes or vendors change:
// every stored choice is then ignored and the banner asks again.

export interface ConsentChoice {
    analytics: boolean;
    marketing: boolean;
}

export interface StoredConsent extends ConsentChoice {
    v: number;
    ts: number;
}

export const OPEN_CONSENT_SETTINGS_EVENT = "emdrlink:open-consent-settings";

const FALLBACK_COOKIE = "emdrlink_consent";
const FALLBACK_VERSION = 1;
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
// Cookies set by tags in GTM-PMZP4R8G (GA4, conversion linker, Contentsquare).
const OPTIONAL_COOKIE_PREFIXES = ["_ga", "_gid", "_gat", "_gcl_", "_cs_"];

const cookieName = () => window.emdrAnalytics?.consentCookie ?? FALLBACK_COOKIE;
const consentVersion = () => window.emdrAnalytics?.consentVersion ?? FALLBACK_VERSION;

export function readConsent(): StoredConsent | null {
    const pair = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(cookieName() + "="));
    if (!pair) {
        return null;
    }
    try {
        const parsed = JSON.parse(decodeURIComponent(pair.slice(cookieName().length + 1)));
        if (
            parsed &&
            parsed.v === consentVersion() &&
            typeof parsed.analytics === "boolean" &&
            typeof parsed.marketing === "boolean"
        ) {
            return parsed as StoredConsent;
        }
    } catch {
        /* malformed cookie: treat as no choice */
    }
    return null;
}

export function consentModeState(choice: ConsentChoice): Record<string, "granted" | "denied"> {
    const ads = choice.marketing ? "granted" : "denied";
    return {
        ad_storage: ads,
        ad_user_data: ads,
        ad_personalization: ads,
        analytics_storage: choice.analytics ? "granted" : "denied",
    };
}

export function saveConsent(choice: ConsentChoice): void {
    const previous = readConsent();
    const value: StoredConsent = {
        v: consentVersion(),
        analytics: choice.analytics,
        marketing: choice.marketing,
        ts: Date.now(),
    };
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
        `${cookieName()}=${encodeURIComponent(JSON.stringify(value))}` +
        `; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;

    gtag("consent", "update", consentModeState(choice));
    // For tags that do not read Consent Mode (e.g. Contentsquare): trigger on
    // this event and check the consent_* variables.
    pushToDataLayer({
        event: "consent_update",
        consent_analytics: choice.analytics,
        consent_marketing: choice.marketing,
        consent_choice_made: true,
    });

    const withdrew =
        previous !== null &&
        ((previous.analytics && !choice.analytics) || (previous.marketing && !choice.marketing));
    if (withdrew) {
        removeOptionalCookies();
    }
}

/** Best effort: expire vendor cookies on this host and every parent domain. */
export function removeOptionalCookies(): void {
    const names = document.cookie
        .split(";")
        .map((part) => part.trim().split("=")[0])
        .filter((name) => OPTIONAL_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix)));
    const labels = window.location.hostname.split(".");
    const domains = [""];
    for (let i = 0; i < labels.length - 1; i++) {
        domains.push("; Domain=." + labels.slice(i).join("."));
    }
    names.forEach((name) => {
        domains.forEach((domain) => {
            document.cookie = `${name}=; Max-Age=0; Path=/${domain}`;
        });
    });
}

export function openConsentSettings(): void {
    window.dispatchEvent(new Event(OPEN_CONSENT_SETTINGS_EVENT));
}
